import { Link } from 'react-router-dom';
import { Mail, Clock, MapPin, Send } from 'lucide-react';
import { useContactForm } from '../hooks/useContactForm';
import Seo from '../components/Seo';
import { AppStoreBadge } from '../components/StoreButtons';

const ContactHero = () => (
  <section className="relative py-20 bg-gradient-to-br from-cyan-600 to-cyan-800 text-white" data-testid="contact-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
        FlyFinder — Now Available on the Apple App Store
      </h1>
      <p className="text-lg md:text-xl text-cyan-100 max-w-3xl mx-auto mb-4">
        Download FlyFinder today and get the exact fly, size, and technique for your water—right now.
      </p>
      <p className="text-cyan-300 font-semibold mb-8">
        Join thousands of experienced anglers fishing smarter
      </p>
      <div className="bg-cyan-700/50 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto border border-cyan-400/30">
        <h3 className="text-xl font-bold mb-4">Why anglers choose FlyFinder:</h3>
        <ul className="text-left space-y-3 text-cyan-100 mb-6">
          {[
            'Real-time, condition-based fly recommendations for your water',
            'Regional hatch data and species-specific emergence windows',
            'Comprehensive fly pattern database with 44+ patterns',
            'Offline mode with smart sync for waters without cell service',
          ].map((benefit) => (
            <li key={benefit} className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#137dc5]/100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 bg-[#1E2328] rounded-full"></div>
              </div>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-center">
          <AppStoreBadge className="h-14" />
        </div>
      </div>
    </div>
  </section>
);

const ContactForm = ({ formData, isSubmitting, submitStatus, handleChange, handleSubmit }) => (
  <div className="bg-[#1E2328] rounded-2xl p-8 shadow-lg" data-testid="contact-form">
    <h2 className="text-3xl font-bold text-white mb-6">Send Us a Message</h2>
    
    {submitStatus && (
      <div data-testid="contact-form-status" className={`mb-6 p-4 rounded-lg ${
        submitStatus.type === 'success' 
          ? 'bg-green-50 border border-green-200 text-green-800' 
          : 'bg-red-50 border border-red-200 text-red-800'
      }`}>
        {submitStatus.message}
      </div>
    )}
    
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-semibold text-slate-300 mb-2">Name *</label>
        <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange}
          className="w-full px-4 py-3 border border-white/15 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all" placeholder="Your full name" />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2">Email *</label>
        <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange}
          className="w-full px-4 py-3 border border-white/15 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all" placeholder="your.email@example.com" />
      </div>
      <div>
        <label htmlFor="phone" className="block text-sm font-semibold text-slate-300 mb-2">Phone</label>
        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
          className="w-full px-4 py-3 border border-white/15 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all" placeholder="(123) 456-7890" />
      </div>
      <div>
        <label htmlFor="subject" className="block text-sm font-semibold text-slate-300 mb-2">Subject *</label>
        <select id="subject" name="subject" required value={formData.subject} onChange={handleChange}
          className="w-full px-4 py-3 border border-white/15 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all">
          <option value="">Select a topic...</option>
          <option value="general">General Inquiry</option>
          <option value="fly-selection">Fly Selection Advice</option>
          <option value="local-conditions">Local Fishing Conditions</option>
          <option value="technical-support">App Technical Support</option>
          <option value="partnership">Partnership Opportunities</option>
          <option value="media">Media Inquiries</option>
          <option value="feedback">Feedback & Suggestions</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-semibold text-slate-300 mb-2">Message *</label>
        <textarea id="message" name="message" required value={formData.message} onChange={handleChange} rows="6"
          className="w-full px-4 py-3 border border-white/15 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
          placeholder="Tell us about your needs or questions..." />
      </div>
      <button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn"
        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
        {isSubmitting ? 'Sending...' : 'Send Message'}
        <Send className="w-5 h-5" />
      </button>
    </form>
  </div>
);

const ContactSidebar = () => (
  <div className="space-y-8">
    <div className="bg-[#151b21] rounded-2xl p-8">
      <h3 className="text-2xl font-bold text-white mb-6">What We Can Help With</h3>
      <ul className="space-y-4">
        {['App technical support and troubleshooting', 'Fly selection advice for North American waters', 'Local fishing condition questions', 'Partnership and collaboration opportunities', 'Media inquiries and interviews', 'Feedback and suggestions for app improvements'].map((item) => (
          <li key={item} className="flex items-start gap-3">
            <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
            <span className="text-slate-300">{item}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl p-8 text-white">
      <h3 className="text-2xl font-bold mb-4">Our Team</h3>
      <p className="text-cyan-50 mb-4">
        Our team is based across Canada and understands the unique challenges of fishing from coast to coast. From the technical streams of Southern Ontario to the pristine lakes of British Columbia, we've been there and we're here to help you succeed on the water.
      </p>
      <p className="text-cyan-50">
        We're passionate about helping anglers find success on the water, and we're always happy to share our knowledge and learn from yours.
      </p>
    </div>

    <div className="bg-[#151b21] rounded-2xl p-8">
      <h3 className="text-2xl font-bold text-white mb-4">For Immediate Help</h3>
      <p className="text-slate-300 mb-4">
        Check out our comprehensive Fly Selection Guide for detailed information about choosing the right flies for North American conditions, or download FlyFinder now on the Apple App Store.
      </p>
      <Link to="/fly-guide" className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 inline-block">
        Explore the Fly Guide
      </Link>
    </div>
  </div>
);

const Contact = () => {
  const form = useContactForm();

  return (
    <div className="min-h-screen bg-[#0F1419]">
      <Seo path="/contact" title="Contact FlyFinder | Support & Questions" description="Get in touch with the FlyFinder team for app support, fly selection advice, partnerships, or media inquiries. Based in Kingston, Ontario, Canada." />
      <ContactHero />

      <section className="py-16 -mt-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#1E2328] rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-4"><Mail className="w-6 h-6 text-[#3aa0e0]" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Email Us</h3>
              <a href="mailto:info@flyfinder.ca" className="text-[#3aa0e0] hover:text-[#3aa0e0] font-medium">info@flyfinder.ca</a>
            </div>
            <div className="bg-[#1E2328] rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-4"><Clock className="w-6 h-6 text-[#3aa0e0]" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Response Time</h3>
              <p className="text-slate-400">Within 24 hours</p>
              <p className="text-sm text-slate-500 mt-1">Mon-Fri, 9 AM - 5 PM EST</p>
            </div>
            <div className="bg-[#1E2328] rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-4"><MapPin className="w-6 h-6 text-[#3aa0e0]" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Location</h3>
              <p className="text-slate-400">Based across Canada</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <ContactForm {...form} />
            <ContactSidebar />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
